import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Calendar, Users, Wallet, BookOpen, TrendingUp, Check, X, Settings } from "lucide-react";

const STATUS = ["pending_payment","payment_review","confirmed","ongoing","completed","cancelled"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState({});
  const [appts, setAppts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [fee, setFee] = useState(300);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const [o, a, p, s] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/appointments"),
      api.get("/admin/ebook-purchases"),
      api.get("/admin/settings"),
    ]);
    setOverview(o.data); setAppts(a.data); setPurchases(p.data); setFee(s.data.consultation_fee);
  };

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/dashboard"); return; }
    load();
  }, [user, nav]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/appointments/${id}`, { status });
    load();
  };
  const updatePurchase = async (id, status) => {
    await api.patch(`/admin/ebook-purchases/${id}`, { status });
    load();
  };
  const saveFee = async () => {
    await api.patch("/admin/settings", { consultation_fee: Number(fee) });
    alert("Fee updated");
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-dashboard">
      <div className="overline mb-1">Admin</div>
      <h1 className="font-display font-black text-3xl mb-6">Doctor's control room</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card icon={Calendar} label="Today's Appts" value={overview.today_appointments || 0}/>
        <Card icon={Wallet} label="Pending Payments" value={overview.pending_payments || 0}/>
        <Card icon={Users} label="Parents" value={overview.parents || 0}/>
        <Card icon={BookOpen} label="E-book Sales" value={overview.ebook_sales || 0}/>
        <Card icon={TrendingUp} label="Confirmed" value={overview.confirmed || 0}/>
        <Card icon={Wallet} label="Revenue ₹" value={overview.revenue || 0}/>
        <Card icon={Calendar} label="Total Appts" value={overview.total_appointments || 0}/>
      </div>

      <div className="flex gap-2 mb-4">
        {["overview","appointments","purchases","settings"].map((t) => (
          <button key={t} onClick={()=>setTab(t)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab===t?'bg-[#2D7A78] text-white':'bg-white border border-[#E5EBEB]'}`} data-testid={`admin-tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "appointments" && (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-appts-table">
            <thead className="text-left text-[#5A6D6D] border-b border-[#E5EBEB]">
              <tr><th className="p-2">Parent</th><th className="p-2">Child</th><th className="p-2">Type</th><th className="p-2">Concern</th><th className="p-2">Fee</th><th className="p-2">Status</th><th className="p-2">Actions</th></tr>
            </thead>
            <tbody>
              {appts.map((a) => (
                <tr key={a.id} className="border-b border-[#E5EBEB]" data-testid={`admin-appt-${a.id}`}>
                  <td className="p-2"><div className="font-semibold">{a.parent_name}</div><div className="text-xs text-[#5A6D6D]">{a.parent_phone}</div></td>
                  <td className="p-2">{a.child_name}</td>
                  <td className="p-2 capitalize">{a.consultation_type}</td>
                  <td className="p-2">{a.concern_category}</td>
                  <td className="p-2">₹{a.fee}</td>
                  <td className="p-2">
                    <select value={a.status} onChange={(e)=>updateStatus(a.id, e.target.value)} className="px-2 py-1 rounded border text-xs" data-testid={`status-select-${a.id}`}>
                      {STATUS.map((s)=><option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-2 flex gap-1">
                    <Link to={`/chat/${a.id}`} className="btn-outline text-xs px-2 py-1">Chat</Link>
                    {a.payment_screenshot && <button onClick={()=>window.open(a.payment_screenshot, "_blank")} className="text-xs underline">Screenshot</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "purchases" && (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[#5A6D6D] border-b border-[#E5EBEB]">
              <tr><th className="p-2">Parent</th><th className="p-2">E-book</th><th className="p-2">Price</th><th className="p-2">Status</th><th className="p-2">Actions</th></tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id} className="border-b border-[#E5EBEB]" data-testid={`admin-purchase-${p.id}`}>
                  <td className="p-2"><div className="font-semibold">{p.parent_name}</div><div className="text-xs text-[#5A6D6D]">{p.parent_email}</div></td>
                  <td className="p-2">{p.ebook_title}</td>
                  <td className="p-2">₹{p.price_paid}</td>
                  <td className="p-2"><span className="chip">{p.status}</span></td>
                  <td className="p-2 flex gap-1">
                    {p.status !== "approved" && <button onClick={()=>updatePurchase(p.id, "approved")} className="btn-primary text-xs px-2 py-1" data-testid={`approve-${p.id}`}><Check size={12}/> Approve</button>}
                    {p.status !== "rejected" && <button onClick={()=>updatePurchase(p.id, "rejected")} className="text-xs px-2 py-1 border border-[#B3261E] text-[#B3261E] rounded-full"><X size={12}/> Reject</button>}
                    {p.payment_screenshot && <button onClick={()=>window.open(p.payment_screenshot,"_blank")} className="text-xs underline">Slip</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && (
        <div className="card-soft max-w-md">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Settings size={18}/> Consultation Settings</h3>
          <label className="text-xs font-semibold text-[#5A6D6D]">CONSULTATION FEE (₹)</label>
          <input type="number" value={fee} onChange={(e)=>setFee(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="admin-fee-input"/>
          <button onClick={saveFee} className="btn-primary mt-3" data-testid="admin-save-fee">Save</button>
        </div>
      )}

      {tab === "overview" && (
        <div className="card-soft">
          <p className="text-sm text-[#5A6D6D]">Welcome back, Dr. Hariom. Use the tabs above to manage appointments, verify payments and adjust settings.</p>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value }) {
  return (
    <div className="card-soft">
      <Icon color="#2D7A78" strokeWidth={1.7} size={20}/>
      <div className="text-2xl font-display font-black mt-2">{value}</div>
      <div className="text-xs text-[#5A6D6D]">{label}</div>
    </div>
  );
}
